import { useState, useEffect } from 'react';
import { useCart } from '../shared/CartContext';
import { supabase } from '../lib/supabaseclient';

export default function Domains() {
  const [query, setQuery] = useState('');
  const [tlds, setTlds] = useState([]);
  const [domainParts, setDomainParts] = useState([]);
  const [loadingTlds, setLoadingTlds] = useState(true);
  const [checkingDomain, setCheckingDomain] = useState(false);
  const [toast, setToast] = useState(null);
  const [result, setResult] = useState(null);

  const { addItem } = useCart();

  // Fetch TLDs from Supabase
  useEffect(() => {
    async function fetchTlds() {
      try {
        console.log('Fetching TLDs from Supabase...');
        const { data, error } = await supabase.from('domain_prices').select('*');
        if (error) {
          console.error('Error fetching TLDs:', error);
        } else {
          console.log('Fetched TLDs:', data);
          setTlds(data);
        }
      } catch (err) {
        console.error('Unexpected error fetching TLDs:', err);
      } finally {
        setLoadingTlds(false);
      }
    }
    fetchTlds();
  }, []);

  const handleCheckDomain = async () => {
    if (!query.includes('.')) {
      setToast({ message: 'Enter a valid domain' });
      setTimeout(() => setToast(null), 1800);
      return;
    }

    const parts = query.split('.');
    setDomainParts(parts);
    const enteredTld = '.' + parts.slice(1).join('.').toLowerCase();

    if (enteredTld.endsWith('.pk')) {
      setToast({ message: 'Unsupported TLD' });
      setTimeout(() => setToast(null), 1800);
      return;
    }

    const tldData = tlds.find(t => t.tld?.toLowerCase() === enteredTld);

    if (!tldData) {
      setToast({ message: 'Enter a valid TLD' });
      setTimeout(() => setToast(null), 1800);
      return;
    }

    setCheckingDomain(true);
    try {
      console.log('Checking domain availability via Supabase function...');
      const res = await fetch(
        'https://btdhzueiqamljnbfgnob.supabase.co/functions/v1/check-domain',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ domain: query }),
        }
      );
      const data = await res.json();
      console.log('Domain check result:', data);
      setResult({ available: data.available, tldData });
    } catch (err) {
      console.error('Error checking domain:', err);
      setToast({ message: 'Error checking domain' });
      setTimeout(() => setToast(null), 1800);
    } finally {
      setCheckingDomain(false);
    }
  };

  const handleAdd = tld => {
    const key = `domain:${query}`;
    addItem(key, {
      title: `${query} domain`,
      subtitle: '1 year registration',
      price: tld.register_price,
    });
    setToast({ message: `${query} added to cart` });
    setTimeout(() => setToast(null), 1800);
  };

  return (
    <div className="text-gray-800">
      {/* Hero */}
      <section className="relative bg-[linear-gradient(160deg,#1e40af_0%,#0891b2_100%)] text-white py-20">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold">Search Domains</h1>
          <p className="mt-4 text-blue-100 max-w-2xl mx-auto text-lg">
            Find the perfect domain name for your business.
          </p>
        </div>
      </section>

      {/* Special Domains CTA */}
        <section className="container mx-auto px-6 py-8">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all flex flex-col sm:flex-row items-center justify-between gap-6">
            {/* Text Content */}
            <div className="text-center sm:text-left flex-1">
              <h3 className="text-2xl font-bold text-blue-700 mb-2">
                Need <span className="text-blue-900">.pk, .com.pk</span> or <span className="text-blue-900">.edu.pk</span> domains?
              </h3>
              <p className="text-gray-700 text-lg">
                We can help you secure these special domains. Contact us directly on WhatsApp for instant assistance.
              </p>
            </div>

            {/* WhatsApp Button */}
            <a
              href={`https://wa.me/923214994888?text=${encodeURIComponent(
                "Hello! I want to inquire about .pk domains."
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white font-semibold px-6 py-4 rounded-full shadow-lg hover:scale-105 transition-transform flex items-center justify-center min-w-[180px]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 32"
                fill="currentColor"
                className="w-6 h-6 mr-2"
              >
                <path d="M16.002 3C9.374 3 4 8.374 4 15.002c0 2.653.853 5.098 2.306 7.086L5 29l7.088-1.844A11.939 11.939 0 0016 27c6.628 0 12.002-5.374 12.002-11.998C28.002 8.374 22.628 3 16.002 3zm0 22.004c-1.865 0-3.606-.509-5.108-1.395l-.366-.216-4.216 1.097 1.13-4.104-.236-.38A9.934 9.934 0 016.002 15C6 9.476 10.478 5 16.002 5 21.526 5 26 9.478 26 15.002S21.526 25.004 16.002 25.004zm5.548-7.475c-.305-.152-1.803-.89-2.083-.99-.28-.102-.484-.152-.688.152s-.79.99-.97 1.19c-.18.203-.357.228-.662.077-.305-.153-1.288-.474-2.452-1.51-.906-.808-1.518-1.81-1.696-2.114-.18-.305-.019-.469.134-.621.138-.138.305-.356.458-.534.152-.18.203-.305.305-.509.101-.203.05-.381-.025-.534-.077-.152-.689-1.663-.945-2.282-.248-.596-.5-.514-.689-.523l-.59-.012c-.203 0-.534.076-.814.38-.28.305-1.07 1.045-1.07 2.55 0 1.504 1.095 2.955 1.248 3.159.152.203 2.153 3.287 5.218 4.609.73.315 1.3.503 1.744.643.732.233 1.398.2 1.924.121.588-.088 1.803-.738 2.058-1.45.254-.713.254-1.323.178-1.45-.075-.127-.28-.203-.584-.356z" />
              </svg>
              Contact on WhatsApp
            </a>
          </div>
        </section>

      {/* Search */}
      <section className="container mx-auto px-6 py-16">
        <div className="flex gap-2 mb-8">
          <input
            className="flex-1 border rounded-lg px-4 py-3"
            placeholder="Enter domain name..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setResult(null); // Clear previous results when typing
            }}
            onFocus={() => setResult(null)} // Optional: also clear if user clicks the input
          />
          <button
            className="px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            onClick={handleCheckDomain}
            disabled={checkingDomain}
          >
            {checkingDomain ? 'Checking...' : 'Check'}
          </button>
        </div>

        {/* Domain Result */}
        {result && (
          <div className="p-4 border rounded-lg bg-white mb-4">
            {result.available ? (
              <div>
                <p className="text-green-600 font-semibold">{query} is available ✅</p>
                <p>Price: RS {result.tldData.register_price}/year</p>
                <button
                  className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                  onClick={() => handleAdd(result.tldData)}
                >
                  Add to cart
                </button>
              </div>
            ) : (
              <div>
                <p className="text-red-600 font-semibold">{query} is not available ❌</p>
                <p>Try these suggestions:</p>
                <ul className="list-disc pl-6 mt-2">
                  {tlds
                    .filter(t => t.category?.toLowerCase().includes('popular'))
                    .slice(0, 3)
                    .map(t => (
                      <li key={t.tld}>
                        {domainParts[0]}{t.tld}
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Popular Domains */}
        <section className="container mx-auto px-6 py-8">
          <h2 className="text-2xl font-semibold mb-4">Popular Domains & Prices</h2>
          {loadingTlds ? (
            <p className="text-gray-500 text-center">Loading popular domains...</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
              {tlds
                .filter(t => t.category?.toLowerCase().includes('popular'))
                .slice(0, 12)
                .map(t => (
                  <div
                    key={t.tld}
                    className="border rounded-xl p-4 text-center shadow hover:shadow-lg transition transform hover:-translate-y-1"
                  >
                    <p className="font-bold text-xl mb-2">{t.tld}</p>
                    <p className="text-gray-700 font-medium">RS {t.register_price}/yr</p>
                  </div>
                ))}
            </div>
          )}
        </section>
        


        {/* Toast */}
        {toast && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white px-4 py-2 rounded-full shadow-lg text-sm">
            {toast.message}
          </div>
        )}
      </section>
    </div>
  );
}
